import { css, styled } from "styled-components";
import React, { useRef } from "react";
import { Icon } from "react-feather";
import { Link } from "react-router-dom";
import Tooltip from "./Tooltip";

export interface Props {
  icon?: Icon;
  iconRight?: boolean;
  label?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  type?: "default" | "success" | "warning" | "danger";
  loading?: boolean;
  link?: string;
  disabled?: boolean;
  borderless?: boolean;
  iconSize?: number;
  className?: string;
  tooltip?: string;
  tooltipPosition?: "top" | "bottom";
  selected?: boolean;
  title?: string;
  tabIndex?: number;
  waiting?: boolean;
  size?: number;
}

const StyledButton = styled.button<{
  $type?: string;
  $borderless?: boolean;
  $loading?: boolean;
  $hasLabel?: boolean;
  $selected?: boolean;
  $disabled?: boolean;
  $size: number;
}>`
  --font-color: var(--gray500);
  color: var(--font-color);
  border: 1px solid transparent;
  font-size: 15px;
  border-radius: 5px;
  padding: 0 ${(props) => Math.floor(props.$size / 6)}px;
  height: ${(props) => props.$size}px;
  max-width: 100%;
  min-width: ${(props) => props.$size}px;
  display: flex;
  align-items: center;
  position: relative;
  justify-content: center;
  white-space: nowrap;
  transition: all 80ms;
  background-color: transparent;
  text-transform: none;
  cursor: pointer;
  font-family: inherit;
  font-weight: unset;
  flex-shrink: 0;
  box-sizing: border-box;

  ${(props) =>
    !props.$hasLabel &&
    css`
      border-radius: 50%;
      padding: 0;
    `}

  ${(props) =>
    !props.$disabled
      ? css`
          &:hover {
            background-color: var(--dark500);
            box-shadow: none;
            border-color: var(--dark600);
          }
        `
      : css`
          &:hover {
            background-color: inherit !important;
          }
        `}

  ${(props) =>
    props.$disabled &&
    css`
      opacity: 0.5;
      cursor: default !important;
    `}

  ${(props) =>
    props.$type === "default" &&
    css`
      --font-color: var(--blue400);
      border: 1px solid var(--blueBorder);
      ${() =>
        !props.$disabled &&
        css`
          &:hover {
            --font-color: var(--blue500);
            border-color: var(--blueBorderOverlay);
            background-color: var(--blueBackground);
          }
        `}
    `}

  ${(props) =>
    props.$type === "success" &&
    css`
      --font-color: var(--green400);
      border: 1px solid var(--greenBorder);
      ${() =>
        !props.$disabled &&
        css`
          &:hover {
            color: var(--green500);
            border-color: var(--greenBorderOverlay);
            background-color: var(--greenBackground);
          }
        `}
    `}

  ${(props) =>
    props.$type === "warning" &&
    css`
      --font-color: var(--orange300);
      border: 1px solid var(--orangeBorder);
      ${() =>
        !props.$disabled &&
        css`
          &:hover {
            --font-color: var(--orange500);
            border-color: var(--orangeBorderOverlay);
            background-color: var(--orangeBackground);
          }
        `}
    `}

  ${(props) =>
    props.$type === "danger" &&
    css`
      --font-color: var(--red400);
      border-color: var(--redBorder);
      ${() =>
        !props.$disabled &&
        css`
          &:hover {
            --font-color: var(--red500);
            border-color: var(--redBorderOverlay);
            background-color: var(--redBackground);
          }
        `}
    `}

  ${(props) =>
    props.$borderless &&
    css`
      border-color: transparent;
      /* border-width: 0 !important; */
    `}

  ${(props) =>
    props.$selected &&
    (props.$type === "danger"
      ? css`
          background-color: var(--red400a);
        `
      : props.$type === "success"
      ? css`
          background-color: var(--green300a);
        `
      : css`
          background-color: var(--dark600);
        `)}

  ${(props) =>
    props.$loading &&
    css`
      > span,
      > i {
        color: transparent;

        &:hover {
          color: transparent;
        }
      }
    `}

  > i {
    display: flex;
    align-items: center;

    ${(props) =>
      props.$hasLabel &&
      css`
        &:first-child {
          padding-left: 2px;
        }

        &:last-child {
          padding-right: 2px;
        }
      `}
  }

  > span {
    padding: 0 3px;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const Loading = styled.div`
  width: 20px;
  height: 20px;
  position: absolute;
  left: 50%;
  top: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 2px solid var(--font-color);
  border-top-color: transparent;
  animation: button_spinner 800ms linear infinite;

  @keyframes button_spinner {
    from {
      transform: translate(-50%, -50%);
    }
    to {
      transform: translate(-50%, -50%) rotate(360deg);
    }
  }
`;

const Waiting = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  box-shadow: -4px -4px 0 0 var(--font-color), -4px 4px 0 0 var(--font-color),
    4px 4px 0 0 var(--font-color);
  animation: dot-bricks 4s infinite ease;

  @keyframes dot-bricks {
    0% {
      box-shadow: -4px -4px 0 0 var(--font-color),
        -4px 4px 0 0 var(--font-color), 4px 4px 0 0 var(--font-color);
    }
    8.333% {
      box-shadow: 4px -4px 0 0 var(--font-color), -4px 4px 0 0 var(--font-color),
        4px 4px 0 0 var(--font-color);
    }
    16.667% {
      box-shadow: 4px -4px 0 0 var(--font-color),
        -4px -4px 0 0 var(--font-color), 4px 4px 0 0 var(--font-color);
    }
    25% {
      box-shadow: 4px -4px 0 0 var(--font-color),
        -4px -4px 0 0 var(--font-color), -4px 4px 0 0 var(--font-color);
    }
    33.333% {
      box-shadow: 4px 4px 0 0 var(--font-color), -4px -4px 0 0 var(--font-color),
        -4px 4px 0 0 var(--font-color);
    }
    41.667% {
      box-shadow: 4px 4px 0 0 var(--font-color), 4px -4px 0 0 var(--font-color),
        -4px 4px 0 0 var(--font-color);
    }
    50% {
      box-shadow: 4px 4px 0 0 var(--font-color), 4px -4px 0 0 var(--font-color),
        -4px -4px 0 0 var(--font-color);
    }
    58.333% {
      box-shadow: -4px 4px 0 0 var(--font-color), 4px -4px 0 0 var(--font-color),
        -4px -4px 0 0 var(--font-color);
    }
    66.666% {
      box-shadow: -4px 4px 0 0 var(--font-color), 4px 4px 0 0 var(--font-color),
        -4px -4px 0 0 var(--font-color);
    }
    75% {
      box-shadow: -4px 4px 0 0 var(--font-color), 4px 4px 0 0 var(--font-color),
        4px -4px 0 0 var(--font-color);
    }
    83.333% {
      box-shadow: -4px -4px 0 0 var(--font-color), 4px 4px 0 0 var(--font-color),
        4px -4px 0 0 var(--font-color);
    }
    91.667% {
      box-shadow: -4px -4px 0 0 var(--font-color),
        -4px 4px 0 0 var(--font-color), 4px -4px 0 0 var(--font-color);
    }
    100% {
      box-shadow: -4px -4px 0 0 var(--font-color),
        -4px 4px 0 0 var(--font-color), 4px 4px 0 0 var(--font-color);
    }
  }
`;

const Button = (props: Props) => {
  const {
    icon,
    label,
    onClick,
    iconRight,
    type,
    loading,
    link,
    disabled,
    borderless,
    iconSize,
    className,
    tooltip,
    tooltipPosition,
    selected,
    title,
    tabIndex,
    waiting,
    size,
  } = props;

  const ref = useRef<HTMLButtonElement>(null);

  const computedIconSize = iconSize ?? (label ? 17 : 19);
  const iconStyles = {
    size: computedIconSize,
    width: computedIconSize,
    height: computedIconSize,
  };
  const button = (active: boolean) => (
    <Tooltip
      label={tooltip}
      placement={tooltipPosition}
      type={type === "danger" ? "danger" : undefined}
    >
      <StyledButton
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          if (!loading && !disabled) {
            onClick?.(e);
          }
          ref.current?.blur();
        }}
        $type={type}
        $borderless={borderless}
        $loading={loading || waiting}
        $disabled={disabled}
        $hasLabel={!!label}
        $selected={selected || active}
        $size={size ?? (label ? 30 : 32)}
        className={className}
        type="button"
        title={title}
        tabIndex={tabIndex}
        ref={ref}
      >
        {icon && !iconRight && <i>{React.createElement(icon, iconStyles)}</i>}
        {label && <span>{label}</span>}
        {icon && iconRight && <i>{React.createElement(icon, iconStyles)}</i>}
        {loading && <Loading />}
        {waiting && <Waiting />}
      </StyledButton>
    </Tooltip>
  );

  return link ? <Link to={link}>{button(false)}</Link> : button(false);
};

export default Button;
